import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../services/apiService';
import { formatMoney } from '../utils/format';
import './CategoryPieChart.css';

// Ordered so neighbouring slices differ in both hue and lightness.
const COLORS = [
  '#5B8DEF',
  '#34D3A6',
  '#A78BFA',
  '#F5A04A',
  '#E9D35B',
  '#F472B6',
  '#38BDF8',
  '#FB7185',
  '#A3E635',
  '#C084FC',
];
const UNCATEGORIZED_COLOR = '#7C8794';

const RADIUS = 70;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SLICE_GAP = 3;

interface MonthlyExpenseItem {
  categoryName: string;
  value: number;
}

interface CategoryPieChartProps {
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
}

const CategoryPieChart: React.FC<CategoryPieChartProps> = ({
  selectedCategory,
  onCategorySelect,
}) => {
  const [data, setData] = useState<MonthlyExpenseItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { getMonthlyExpenses } = useApi();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getMonthlyExpenses();
        setData(response.items);
        setTotal(response.total);
      } catch (err) {
        console.error('Error fetching monthly expenses:', err);
        setError('Failed to load spending.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const slices = useMemo(() => {
    const items = data
      .map((item) => ({
        name: item.categoryName,
        value: Math.abs(Number(item.value)),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    const sum = items.reduce((acc, item) => acc + item.value, 0) || 1;

    let colorIndex = 0;
    let offset = 0;
    return items.map((item) => {
      const length = (item.value / sum) * CIRCUMFERENCE;
      const slice = {
        ...item,
        share: Math.round((item.value / sum) * 100),
        color:
          item.name === 'Uncategorized'
            ? UNCATEGORIZED_COLOR
            : COLORS[colorIndex++ % COLORS.length],
        dashArray: `${Math.max(length - SLICE_GAP, 0)} ${CIRCUMFERENCE}`,
        dashOffset: -offset,
      };
      offset += length;
      return slice;
    });
  }, [data]);

  const hasSelection = slices.some((slice) => slice.name === selectedCategory);
  const monthLabel = new Date().toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    month: 'long',
    year: 'numeric',
  });

  return (
    <section
      className="card category-pie-chart"
      aria-labelledby="spending-title"
    >
      <div className="section-header">
        <div>
          <h2 id="spending-title" className="card-title">
            Spending this month
          </h2>
          <p className="card-subtitle">
            {monthLabel} · select a category to see its transactions
          </p>
        </div>
      </div>

      {loading && <p className="muted">Loading spending...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && slices.length === 0 && (
        <p className="muted">No spending recorded this month.</p>
      )}

      {slices.length > 0 && (
        <div className="category-body">
          <div className="donut">
            <svg
              width="200"
              height="200"
              viewBox="0 0 180 180"
              role="img"
              aria-label={`Spending by category, ${formatMoney(Math.abs(total), 0)} total`}
            >
              <g transform="rotate(-90 90 90)">
                {slices.map((slice) => (
                  <circle
                    key={slice.name}
                    className="donut-slice"
                    cx="90"
                    cy="90"
                    r={RADIUS}
                    fill="none"
                    stroke={slice.color}
                    strokeWidth={22}
                    strokeDasharray={slice.dashArray}
                    strokeDashoffset={slice.dashOffset}
                    opacity={
                      !hasSelection || slice.name === selectedCategory
                        ? 1
                        : 0.35
                    }
                    onClick={() => onCategorySelect(slice.name)}
                  />
                ))}
              </g>
            </svg>
            <div className="donut-center">
              <span className="muted">Total spent</span>
              <span className="donut-total num">
                {formatMoney(Math.abs(total), 0)}
              </span>
            </div>
          </div>

          <ul className="category-legend">
            {slices.map((slice) => {
              const isSelected = slice.name === selectedCategory;
              return (
                <li key={slice.name}>
                  <button
                    className={`category-option${isSelected ? ' is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => onCategorySelect(slice.name)}
                  >
                    <span
                      className="category-dot"
                      style={{ background: slice.color }}
                    />
                    <span className="category-name" title={slice.name}>
                      {slice.name}
                    </span>
                    <span className="category-share num">{slice.share}%</span>
                    <span className="category-amount num">
                      {formatMoney(slice.value, 0)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default CategoryPieChart;
